'use client';

import type {
	SafeParseReturnType,
	ZodObject,
} from 'zod';
import { parse, stringify } from 'qs';
import { useRouter, useSearchParams } from 'next/navigation';

type GenericValue = number | number[] | string | string[] | undefined;

export type UseQueryParamsResult<T> = {
	queryParams:      T;
	setQueryParam:    <K extends keyof T>(key: K, value: GenericValue ) => void;
	removeQueryKey:   (key: keyof T) => void;
	clearQueryParams: () => void;
};

export const useQueryParams = <T extends object>(
	schema: ZodObject<any>,
): UseQueryParamsResult<T> => {
	const router = useRouter();
	const searchParams = useSearchParams();
	const params = parse(searchParams.toString(), { ignoreQueryPrefix: true }) as Record<string, unknown>;
	const parsedResult = schema.safeParse(params) as unknown as SafeParseReturnType<T, T>;
	const defaultParams = schema.safeParse({}).success ? schema.safeParse({}).data : {} as T;
	const queryParams = (parsedResult.success ? parsedResult.data : defaultParams) as T;

	const setQueryParam = <K extends keyof T>(key: K, value: GenericValue ) => {
		const updatedParams = {
			...queryParams,
			[key]: value,
		};

		const queryString = stringify(updatedParams, {
			arrayFormat:    'brackets',
			encode:					false,
			skipNulls:      true,
			addQueryPrefix: true,
		});

		router.push(queryString);
	};

	const removeQueryKey = (key: keyof T) => {
		const updatedParams = { ...queryParams } as Record<string, any>;
		delete updatedParams[key as string];

		const queryString = stringify(updatedParams, {
			arrayFormat:    'brackets',
			skipNulls:      true,
			addQueryPrefix: true,
		});

		router.push(queryString);
	};

	const clearQueryParams = () => router.push('?');

	return {
		queryParams,
		setQueryParam,
		removeQueryKey,
		clearQueryParams,
	};
};